import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiCheckLine, RiLockLine, RiArrowDownSLine, RiAlertLine,
  RiFireLine, RiRefreshLine,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import { SECTIONS, nodeHref } from '../../data/curriculum';
import { useTourActive } from '../../components/tour/TourContext';
import { getRoadmap, completeRoadmapLesson, unlockRoadmapSection, resetRoadmapSection } from '../../api/quiz.api';
import { LoadingScreen } from '../../components/shared/UI';
import KitsuMark from '../../components/brand/KitsuMark';

/* Geometry for the winding path. Rows are a fixed height so the connector
   between two nodes can be computed exactly rather than guessed.
   The weave is symmetric around 0 so the path snakes down the *centre*
   of the column rather than drifting off to one side. */
const ROW_H = 158;     // px, centre-to-centre
const WEAVE = [0, 34, 68, 34, 0, -34, -68, -34];

/* Half of .learn-node-face's 68px. A row is positioned by its top edge,
   so this is how far down that row its node's centre line sits — the
   line the scenery beside it has to line up with. */
const NODE_R = 34;

/* The same ±68px swing that reads as a wave in a 347px column looks nearly
   straight in a 656px one, so the amplitude scales with the space available.
   It is applied in JS rather than CSS because the connector's length and
   angle are computed from the same numbers — scaling only the row transform
   would leave the joins pointing at where the nodes used to be. */
/* Blobs parked in the hollow of each bend.

   The weave only ever uses the middle of a wide screen, which leaves two
   tall dead margins either side. Duolingo fills exactly that space with
   its owl, and the reason it works is that the mascot is *off* the path:
   it is scenery, so it never competes with the node you mean to press.

   Placement is not free choice. A blob dropped on an arbitrary row reads
   as debris; it only looks deliberate when it sits at the apex of a
   curve, tucked into the hollow the bend leaves behind. WEAVE peaks at
   ±68, so those rows are exactly where the path swings furthest from
   centre and the opposite margin is at its emptiest — one blob per bend,
   alternating sides for free because the weave alternates. */
const WEAVE_PEAK = Math.max(...WEAVE.map(Math.abs));

/* Idle loops. One of these runs continuously so a resting blob still
   breathes rather than sitting frozen like a broken image. */
const DECOR_IDLE = ['float', 'breathe', 'sleep'];

/* ...and every so often it does something. These are one-shots, so they
   have to be handed back to the idle loop by hand once the CSS finishes
   — `ms` must match the catalogue in KitsuMark.

   The prop scenes (coffee, headphones, rain, the lightbulb) were tried
   here and taken back out. They are about the blob rather than about what
   you are doing, and as recurring scenery down the margin they competed
   with the lesson nodes instead of decorating them. They still exist in
   the catalogue and on the review page; they are just not scenery. */
const DECOR_TRICKS = [
  { name: 'bounce',    ms: 900 },
  { name: 'nod',       ms: 800 },
  { name: 'wobble',    ms: 800 },
  { name: 'pop',       ms: 600 },
  { name: 'celebrate', ms: 2100 },
];

const TRICK_MIN_MS = 4000;
const TRICK_JITTER_MS = 7000;

const decorFor = (i) => {
  const weave = WEAVE[i % WEAVE.length];
  if (Math.abs(weave) !== WEAVE_PEAK) return null;

  const n = Math.floor(i / (WEAVE.length / 2));
  return {
    side: weave > 0 ? -1 : 1,      // into the hollow, opposite the bulge
    idle: DECOR_IDLE[n % DECOR_IDLE.length],
    /* Deterministic variety — a render must not reshuffle the scenery. */
    size: 78 + (n % 3) * 12,
  };
};

/* One blob. Idles, and every 4–11s plays a trick and settles back.

   The stagger is random per blob rather than an index offset: a fixed
   offset makes the column pulse in a visible wave, which is worse than
   no motion at all. */
function PathDecor({ idle, size }) {
  const [anim, setAnim] = useState(idle);
  const [run, setRun] = useState(0);

  useEffect(() => {
    if (typeof window !== 'undefined'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let toTrick, toIdle;
    const schedule = () => {
      toTrick = setTimeout(() => {
        const trick = DECOR_TRICKS[Math.floor(Math.random() * DECOR_TRICKS.length)];
        setAnim(trick.name);
        setRun(r => r + 1);          // bumping this is what replays the CSS
        toIdle = setTimeout(() => { setAnim(idle); schedule(); }, trick.ms + 120);
      }, TRICK_MIN_MS + Math.random() * TRICK_JITTER_MS);
    };
    schedule();
    return () => { clearTimeout(toTrick); clearTimeout(toIdle); };
  }, [idle]);

  return (
    <KitsuMark
      size={size}
      animation={anim}
      animationRun={run}
      interactive={false}
      glow={false}
    />
  );
}

const DESKTOP_WEAVE_SCALE = 2;
const DESKTOP_QUERY = '(min-width: 900px)';

function useWeaveScale() {
  const [scale, setScale] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
      ? DESKTOP_WEAVE_SCALE
      : 1
  ));

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const apply = () => setScale(mq.matches ? DESKTOP_WEAVE_SCALE : 1);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return scale;
}

/* Segmented ring around a node — one segment per step of that node.
   A staged kana row has three or four, so finishing a single exercise
   fills a visible quarter of the ring. Count-based nodes keep the
   ten-segment mastery scale the server sends. */
const RING_R = 40;
const RING_CIRC = 2 * Math.PI * RING_R;
const RING_GAP = 6; // px of arc between segments

function SegmentRing({ segments, filled, done }) {
  const step = RING_CIRC / segments;
  const seg = Math.max(2, step - RING_GAP);
  return (
    <svg className="learn-ring" viewBox="0 0 96 96" aria-hidden="true">
      {Array.from({ length: segments }, (_, i) => (
        <circle
          key={i}
          className={`learn-ring-seg${i < filled ? ' on' : ''}${done ? ' done' : ''}`}
          cx="48" cy="48" r={RING_R}
          strokeDasharray={`${seg} ${RING_CIRC - seg}`}
          strokeDashoffset={-(i * step)}
        />
      ))}
    </svg>
  );
}

export default function LearnPath() {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState(null);
  const [steps, setSteps] = useState(10); // ring segments, told to us by the server
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [openSections, setOpenSections] = useState(null); // Set of section ids
  const [unlocked, setUnlocked] = useState([]);   // section ids opened out of order
  const [lastLessonId, setLastLessonId] = useState('');  // where work actually happened
  /* An explicit jump this session. Unlocking a section is a statement of
     intent — "take me there now" — but it is not studying, so it must not
     outlive the visit. Once you leave and come back, `lastLessonId` (real
     work) is what decides where the path opens. */
  const [jumpTo, setJumpTo] = useState(null);
  const [askUnlock, setAskUnlock] = useState(null); // section pending confirmation
  const [unlocking, setUnlocking] = useState(false);
  const [askReset, setAskReset] = useState(null);   // section pending reset
  const [resetting, setResetting] = useState(false);
  const tourActive = useTourActive();
  /* Tapping a node opens this bubble rather than dropping straight into the
     exercise. Landing mid-drill because of a stray tap is the kind of thing
     that costs a streak, and the bubble is also the only place we can say
     what the step actually is before committing to it. */
  const [openNode, setOpenNode] = useState(null); // { node, section, stage, segs, filledSegs }
  const currentRef = useRef(null);

  useEffect(() => {
    if (!openNode) return undefined;
    const onKey = e => { if (e.key === 'Escape') setOpenNode(null); };
    /* Anything outside the bubble dismisses it. The node itself is excluded
       so its own handler can toggle rather than being closed here and
       reopened on the same click. */
    const onDown = e => {
      if (e.target.closest?.('.learn-pop, .learn-node')) return;
      setOpenNode(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [openNode]);
  const hasScrolled = useRef(false);
  const waveScale = useWeaveScale();
  const weaveAt = useCallback(i => WEAVE[i % WEAVE.length] * waveScale, [waveScale]);

  const load = useCallback(() => {
    setError('');
    getRoadmap()
      .then(res => {
        setLessons(res.data.lessons);
        setUnlocked(res.data.unlockedSections || []);
        setLastLessonId(res.data.lastLessonId || '');
        if (res.data.steps) setSteps(res.data.steps);
      })
      .catch(err => setError(err.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Walk the path in order: a node unlocks when the one before it is done,
     so within a section there is exactly one "current" node and everything
     after it is locked.

     Sections are the unit that can be skipped. Strict order is right for a
     true beginner and wrong for someone who already reads kana and only
     wants the kanji — so a section opens either because everything before
     it is finished, or because the learner explicitly unlocked it. An
     unlock opens the rows; it never marks any of them done. */
  const { sections, activeSectionId, totalDone, totalNodes } = useMemo(() => {
    if (!lessons) {
      return { sections: [], activeSectionId: null, totalDone: 0, totalNodes: 0 };
    }

    const unlockedSet = new Set(unlocked);
    let priorComplete = true;      // is everything before this section done?
    let naturalId = null;          // where strict order says you are
    let skippedId = null;          // the furthest section you chose to jump to
    let done = 0;
    let count = 0;

    const out = SECTIONS.map(section => {
      const openable = priorComplete || unlockedSet.has(section.id);

      let reachedCurrent = false;
      const nodes = section.nodes.map(node => {
        const info = lessons[node.id] || { done: false, have: 0, need: 1, auto: true, ratio: 0, filled: 0 };
        count += 1;
        if (info.done) done += 1;

        let state;
        if (info.done) {
          state = 'done';
        } else if (openable && !reachedCurrent) {
          state = 'current';
          reachedCurrent = true;
        } else {
          state = 'locked';
        }
        return { ...node, ...info, state };
      });

      const sectionDone = nodes.filter(n => n.done).length;
      const complete = sectionDone === nodes.length;

      if (!complete) {
        if (priorComplete && !naturalId) naturalId = section.id;
        if (unlockedSet.has(section.id)) skippedId = section.id;
      }

      const locked = !openable;
      if (!complete) priorComplete = false;

      return {
        ...section,
        nodes,
        sectionDone,
        sectionTotal: nodes.length,
        complete,
        locked,
        skipped: unlockedSet.has(section.id),
      };
    });

    /* Which section the path opens on, in order of how much the choice was
       actually earned:

       1. A jump made in this visit. You pressed "Jump here" ten seconds
          ago; go there.
       2. The section holding the last lesson you worked on. This is the
          one that was missing, and its absence caused a specific bug:
          unlocking section 6 out of curiosity made `skippedId` the answer
          forever, so studying katakana and pressing "Back to path" kept
          scrolling to the exam section — somewhere no work had ever
          happened. Opening a section and studying in one are different
          events, and only the second means "this is where I am".
       3. The furthest section you unlocked, for the visit after a jump
          but before any work in it.
       4. Where strict order says you are. */
    const lastSection = out.find(
      sec => !sec.complete && !sec.locked && sec.nodes.some(n => n.id === lastLessonId),
    );
    const jumped = out.find(sec => sec.id === jumpTo && !sec.complete);

    /* Anyone who was already using the app before lastLessonId existed has
       an empty one, and would keep getting the old wrong answer until they
       happened to finish an exercise. Real progress is the next best
       evidence of where they were: take the furthest section with work in
       it, which beats "the furthest section you once unlocked" — unlocking
       leaves 0/3 behind, studying does not. */
    const withProgress = [...out].reverse().find(
      sec => !sec.complete && !sec.locked && sec.sectionDone > 0,
    );

    const activeId = jumped?.id || lastSection?.id || withProgress?.id || skippedId || naturalId;
    return { sections: out, activeSectionId: activeId, totalDone: done, totalNodes: count };
  }, [lessons, unlocked, lastLessonId, jumpTo]);

  /* Only the section you're on is expanded to begin with — 42 open rows at
     once is a wall of grey locks rather than something you can navigate. */
  useEffect(() => {
    if (!activeSectionId || openSections) return;
    setOpenSections(new Set([activeSectionId]));
  }, [activeSectionId, openSections]);

  /* Come back from a lesson and the node you are about to do should be
     under your eyes, not somewhere below the fold.

     The timing is the whole difficulty. A row stays mounted while its
     section is collapsed — `.learn-collapse` animates grid-template-rows
     from 0fr, it does not unmount anything — so `currentRef` is already
     non-null on the very first pass, when the section is still shut and
     the row is squashed at the top. Scrolling then aimed at a position
     the node was about to leave, and because the guard was claimed up
     front there was never a second attempt: you landed high on the page
     and had to scroll down by hand.

     So wait for the section to be opened, then for its 380ms expand to
     finish, and measure only after that. The timer is the fallback for
     every case where transitionend never arrives — section already open,
     reduced motion, or a backgrounded tab. */
  useEffect(() => {
    if (!lessons || hasScrolled.current || totalDone === 0 || !openSections) return;
    const el = currentRef.current;
    if (!el) return;
    hasScrolled.current = true;

    const collapse = el.closest('.learn-collapse');
    let settled = false;
    const go = () => {
      if (settled) return;
      settled = true;
      collapse?.removeEventListener('transitionend', onEnd);
      currentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const onEnd = e => { if (e.propertyName === 'grid-template-rows') go(); };

    collapse?.addEventListener('transitionend', onEnd);
    const timer = setTimeout(go, 450);
    return () => {
      settled = true;
      clearTimeout(timer);
      collapse?.removeEventListener('transitionend', onEnd);
    };
  }, [lessons, totalDone, openSections]);

  const toggleSection = id => {
    /* The tour spotlights the current node, and a collapsed section keeps
       its rows mounted at zero height — so collapsing the section it is
       pointing at leaves the spotlight measuring a 0px box and falling
       back to a centred card. Refuse rather than let the reader break the
       thing mid-explanation. */
    if (tourActive && id === activeSectionId) return;
    setOpenSections(prev => {
      const next = new Set(prev || []);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /* And open it in the first place. Someone who collapsed their section
     before starting the tour would otherwise get the same dead spotlight
     from the other direction. */
  useEffect(() => {
    if (!tourActive || !activeSectionId) return;
    setOpenSections(prev => {
      if (prev?.has(activeSectionId)) return prev;
      return new Set([...(prev || []), activeSectionId]);
    });
  }, [tourActive, activeSectionId]);

  const confirmReset = async () => {
    if (!askReset) return;
    setResetting(true);
    try {
      const res = await resetRoadmapSection(askReset.id);
      setUnlocked(res.data.unlockedSections);
      if (jumpTo === askReset.id) setJumpTo(null);
      setOpenSections(prev => {
        const next = new Set(prev || []);
        next.delete(askReset.id);
        return next;
      });
      hasScrolled.current = false;
      setAskReset(null);
      load();                        // re-derive every node from the server
    } catch (err) {
      setError(err.message);
    } finally {
      setResetting(false);
    }
  };

  const confirmUnlock = async () => {
    if (!askUnlock) return;
    setUnlocking(true);
    try {
      const res = await unlockRoadmapSection(askUnlock.id);
      setUnlocked(res.data.unlockedSections);
      setJumpTo(askUnlock.id);       // this visit only — see the jumpTo comment
      setOpenSections(new Set([askUnlock.id]));
      hasScrolled.current = false;   // re-centre on the section just opened
      setAskUnlock(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUnlocking(false);
    }
  };

  const markManual = async (node) => {
    setBusyId(node.id);
    try {
      await completeRoadmapLesson({ lessonId: node.id, done: true });
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !lessons) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: C.red, fontSize: 13, marginBottom: 16 }}>{error}</div>
        <button className="btn-ghost" onClick={load}>Try again</button>
      </div>
    );
  }

  if (!lessons) return <LoadingScreen label="Loading your path" />;

  const overallPct = totalNodes ? Math.round((totalDone / totalNodes) * 100) : 0;

  return (
    <div className="page-container learn-page anim-fade-up">
      <header className="learn-hero">
        <div className="learn-hero-copy">
          <span className="learn-hero-kicker">JLPT N5</span>
          <h1>Learn Japanese</h1>
          <p>Work down the path — each step unlocks the next.</p>
        </div>
        <div className="learn-hero-ring" style={{ '--pct': overallPct }}>
          <span>{overallPct}<i>%</i></span>
          <small>{totalDone}/{totalNodes}</small>
        </div>
      </header>

      {sections.map(section => {
        const open = openSections?.has(section.id);
        const pct = Math.round((section.sectionDone / section.sectionTotal) * 100);
        const isActive = section.id === activeSectionId;

        return (
          <section
            key={section.id}
            className={`learn-section${open ? ' open' : ''}${section.complete ? ' complete' : ''}${section.locked ? ' locked' : ''}`}
            style={{ '--sec-color': section.color }}
          >
            <button
              type="button"
              className="learn-section-banner"
              onClick={() => toggleSection(section.id)}
              aria-expanded={!!open}
            >
              <span className="learn-section-glyph" aria-hidden="true">{section.glyph}</span>

              <span className="learn-section-copy">
                <span className="learn-section-kicker">
                  {section.kicker}
                  {isActive && <em>· in progress</em>}
                  {section.complete && <em>· complete</em>}
                  {section.locked && <em>· locked</em>}
                  {section.skipped && !section.complete && !isActive && <em>· unlocked</em>}
                </span>
                <strong>{section.title}</strong>
                <span className="learn-section-sub">{section.sub}</span>
                <span className="learn-section-bar">
                  <i style={{ width: `${pct}%` }} />
                </span>
              </span>

              <span className="learn-section-right">
                {/* A locked section is still worth opening for someone who
                    already knows the material. The button is a span rather
                    than a nested <button>, which is invalid inside the
                    banner button — the click is caught here and stopped
                    before it toggles the section. */}
                {section.locked && (
                  <span
                    role="button"
                    tabIndex={0}
                    className="learn-section-jump"
                    onClick={e => { e.stopPropagation(); setAskUnlock(section); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); e.stopPropagation(); setAskUnlock(section);
                      }
                    }}
                  >
                    Jump here
                  </span>
                )}
                {/* Undo. Reachable for any section with something to undo —
                    an out-of-order unlock, or work you want to redo from
                    scratch. Same span-not-button trick as Jump here, for
                    the same reason: nesting a button is invalid markup. */}
                {(section.skipped || section.sectionDone > 0) && !section.locked && (
                  <span
                    role="button"
                    tabIndex={0}
                    className="learn-section-reset"
                    title={`Reset ${section.title}`}
                    aria-label={`Reset ${section.title}`}
                    onClick={e => { e.stopPropagation(); setAskReset(section); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); e.stopPropagation(); setAskReset(section);
                      }
                    }}
                  >
                    <RiRefreshLine size={14} />
                  </span>
                )}
                <span className="learn-section-count">
                  {section.complete ? <RiCheckLine size={16} /> : `${section.sectionDone}/${section.sectionTotal}`}
                </span>
                <RiArrowDownSLine className="learn-section-chevron" size={20} />
              </span>
            </button>

            <div className="learn-collapse" style={{ gridTemplateRows: open ? '1fr' : '0fr' }}>
              <div>
                <div className="learn-path" style={{ height: section.nodes.length * ROW_H }}>
                  {/* Scenery first, so it paints under every row. */}
                  {section.nodes.map((node, i) => {
                    const d = decorFor(i);
                    if (!d) return null;
                    return (
                      <span
                        key={`decor-${node.id}`}
                        className="learn-decor"
                        aria-hidden="true"
                        style={{ top: i * ROW_H + NODE_R, '--side': d.side, '--decor-size': `${d.size}px` }}
                      >
                        <PathDecor idle={d.idle} size={d.size} />
                        <span className="learn-decor-shadow" />
                      </span>
                    );
                  })}

                  {section.nodes.map((node, i) => {
                    const isCurrent = node.state === 'current';
                    const segs = node.steps || steps;
                    const filledSegs = node.done ? segs : (node.filled || 0);
                    const stage = node.stages?.[node.nextStage >= 0 ? node.nextStage : 0];

                    // Connector runs from this node's centre to the next one's.
                    const next = section.nodes[i + 1];
                    const dx = next ? weaveAt(i + 1) - weaveAt(i) : 0;
                    const len = next ? Math.hypot(dx, ROW_H) : 0;
                    const angle = next ? -Math.atan2(dx, ROW_H) * (180 / Math.PI) : 0;

                    return (
                      <div
                        key={node.id}
                        className={`learn-row ${node.state}${openNode?.node.id === node.id ? ' popped' : ''}`}
                        style={{ top: i * ROW_H, '--weave': `${weaveAt(i)}px`, '--i': i }}
                        ref={isCurrent && section.id === activeSectionId ? currentRef : null}
                      >
                        {next && (
                          <span
                            className={`learn-connector${node.state === 'done' ? ' filled' : ''}`}
                            style={{ height: len, transform: `rotate(${angle}deg)` }}
                            aria-hidden="true"
                          />
                        )}

                        <button
                          type="button"
                          className={`learn-node ${node.state}${node.boss ? ' boss' : ''}`}
                          /* Scoped to the active section, exactly like
                              `currentRef` above. Every openable section has a
                              node in the 'current' state, so an unscoped hook
                              put a `learn-current` in each one — and the tour,
                              taking the first in document order, spotlighted
                              Section 1 no matter which section you were
                              actually on. One hook, one node. */
                          data-tour={isCurrent && section.id === activeSectionId ? 'learn-current' : undefined}
                          onClick={() => {
                            if (node.state === 'locked') return;
                            setOpenNode(prev => (prev?.node.id === node.id
                              ? null
                              : { node, section, stage, segs, filledSegs }));
                          }}
                          aria-expanded={openNode?.node.id === node.id}
                          disabled={node.state === 'locked'}
                          title={node.state === 'locked' ? 'Finish the step above first' : node.title}
                          aria-label={`${node.title} — ${node.state}`}
                        >
                          {isCurrent && (
                            <span className={`learn-start-pill ${weaveAt(i) > 0 ? 'left' : 'right'}`}>Start</span>
                          )}
                          <span className="learn-node-face">
                            {node.state === 'done'
                              ? <RiCheckLine size={26} />
                              : node.state === 'locked'
                                ? <RiLockLine size={18} />
                                : <span className="learn-node-glyph">{node.icon}</span>}
                          </span>
                          {isCurrent && <SegmentRing segments={segs} filled={filledSegs} done={false} />}
                          {node.state === 'done' && <SegmentRing segments={segs} filled={segs} done />}
                        </button>

                        {openNode?.node.id === node.id && (
                          <div className="learn-pop" role="dialog" aria-label={node.title}>
                            <span className="learn-pop-arrow" aria-hidden="true" />
                            <strong className="learn-pop-title">{node.title}</strong>
                            <span className="learn-pop-sub">
                              {node.auto
                                ? `${stage ? stage.label : 'Practice'} · step ${Math.min(filledSegs + 1, segs)} of ${segs}`
                                : node.sub}
                            </span>
                            <button
                              type="button"
                              className="btn-primary learn-pop-go"
                              onClick={() => navigate(nodeHref(node, section, node))}
                            >
                              {node.state === 'done' ? 'Practise again' : 'Start'}
                            </button>
                          </div>
                        )}

                        <div className="learn-row-meta">
                          <strong>{node.title}</strong>

                          {/* The active row swaps its reading for a session
                              count rather than stacking both — that keeps every
                              row the same height, which the fixed-pitch wave
                              geometry depends on. */}
                          {!(isCurrent && node.auto) && (
                            <span className="learn-row-sub">{node.sub}</span>
                          )}

                          {isCurrent && node.auto && (
                            <span className="learn-row-steps">
                              {stage ? stage.label : 'Tap to practise'} · <b>{filledSegs}</b> of {segs}
                            </span>
                          )}

                          {isCurrent && !node.auto && (
                            <button
                              type="button"
                              className="learn-row-manual"
                              disabled={busyId === node.id}
                              onClick={e => { e.stopPropagation(); markManual(node); }}
                            >
                              {busyId === node.id ? 'Saving…' : 'Mark as done'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <div className="learn-finish">
        <RiFireLine size={15} />
        {totalDone === totalNodes
          ? 'N5 path complete — おめでとう!'
          : `${totalNodes - totalDone} steps left to finish N5`}
      </div>

      {/* Skipping ahead is allowed but never silent. The path is ordered
          the way it is for a reason, and someone who jumps to kanji without
          being able to read kana will have a bad time — so say so plainly,
          name what is being skipped, and let them decide. */}
      {askUnlock && (
        <div
          className="learn-unlock-scrim"
          role="dialog"
          aria-modal="true"
          aria-label={`Jump to ${askUnlock.title}`}
          onClick={e => { if (e.target === e.currentTarget) setAskUnlock(null); }}
        >
          <div className="learn-unlock-card" style={{ '--sec-color': askUnlock.color }}>
            <span className="learn-unlock-icon" aria-hidden="true"><RiAlertLine size={22} /></span>
            <strong>Jump to {askUnlock.title}?</strong>
            <p>
              You have not finished the sections before this one. {askUnlock.title} builds
              on them, so parts of it will be harder than they need to be.
            </p>
            <p className="learn-unlock-note">
              Nothing gets marked as done — the earlier sections stay exactly where
              they are, and you can come back to them whenever you like.
            </p>
            <div className="learn-unlock-actions">
              <button type="button" className="btn-ghost" onClick={() => setAskUnlock(null)} disabled={unlocking}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={confirmUnlock} disabled={unlocking}>
                {unlocking ? 'Unlocking…' : 'Jump here anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {askReset && (
        <div
          className="learn-unlock-scrim"
          role="dialog"
          aria-modal="true"
          aria-label={`Reset ${askReset.title}`}
          onClick={e => { if (e.target === e.currentTarget) setAskReset(null); }}
        >
          <div className="learn-unlock-card" style={{ '--sec-color': askReset.color }}>
            <span className="learn-unlock-icon" aria-hidden="true"><RiRefreshLine size={22} /></span>
            <strong>Reset {askReset.title}?</strong>
            <p>
              Every step in this section goes back to unstarted{askReset.skipped
                ? ', and the section locks again until you reach it in order'
                : ''}. You can redo it from the beginning whenever you like.
            </p>
            {/* Said plainly because it would otherwise look like a bug.
                Words and Kanji steps complete from real mastery, which is
                shared with flashcards and the review schedule — throwing
                that away because someone reset the wrong section would be
                worse than the problem they were solving. */}
            <p className="learn-unlock-note">
              Your flashcard and character mastery is not affected, so any step
              that completes from mastery rather than practice will fill in again.
            </p>
            <div className="learn-unlock-actions">
              <button type="button" className="btn-ghost" onClick={() => setAskReset(null)} disabled={resetting}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={confirmReset} disabled={resetting}>
                {resetting ? 'Resetting…' : 'Reset section'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
